/**
 * Router-aware обёртки над примитивами дизайн-системы: настоящие ссылки (`<a href>`) для
 * доступности и open-in-new-tab, но переходы перехватываются SPA-роутером. Внутренности
 * компонентов ДС не дублируются и не меняются — только навигационное поведение.
 */
import { type MouseEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link, type LinkProps } from '@/design-system/components/primitives/link'
import { NavItem } from '@/design-system/components/layout/nav-item'

/** true, если клик надо обработать SPA-навигацией (без модификаторов/средней кнопки). */
function isPlainLeftClick(e: MouseEvent): boolean {
  return !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
}

export interface AppLinkProps extends Omit<LinkProps, 'href'> {
  to: string
  /**
   * history state перехода (D1, p3-structure-sout-training §2) — напр. `{ from: '/ot' }`, чтобы
   * кнопка «Назад» в целевой карточке (`useBackTarget`) вернула именно сюда.
   */
  state?: unknown
}

/** Текстовая ссылка ДС с переходом через роутер. */
export function AppLink({ to, state, onClick, children, ...rest }: AppLinkProps) {
  const navigate = useNavigate()
  return (
    <Link
      href={to}
      onClick={(e) => {
        onClick?.(e)
        if (isPlainLeftClick(e)) {
          e.preventDefault()
          navigate(to, state !== undefined ? { state } : undefined)
        }
      }}
      {...rest}
    >
      {children}
    </Link>
  )
}

export interface NavLinkItemProps {
  to: string
  active?: boolean
  icon?: ReactNode
  /** Значок справа от подписи (см. `NavItem.trailing`). */
  trailing?: ReactNode
  /** Перенос длинной подписи вместо усечения (см. `NavItem.wrap`). */
  wrap?: boolean
  children: ReactNode
}

/** Пункт сайдбара ДС с переходом через роутер и подсветкой активного. */
export function NavLinkItem({ to, active, icon, trailing, wrap, children }: NavLinkItemProps) {
  const navigate = useNavigate()
  return (
    <NavItem
      href={to}
      active={active}
      icon={icon}
      trailing={trailing}
      wrap={wrap}
      onClick={(e) => {
        if (isPlainLeftClick(e)) {
          e.preventDefault()
          navigate(to)
        }
      }}
    >
      {children}
    </NavItem>
  )
}
